import { Children, isValidElement, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { useId } from '../../hooks/useId'
import styles from './Modal.module.css'

const CLOSE_ANIMATION_MS = 200

function ModalFooter({ children }) {
  return <div className={styles.footer}>{children}</div>
}
ModalFooter.propTypes = { children: PropTypes.node }

function ModalActions({ children }) {
  return <div className={styles.actions}>{children}</div>
}
ModalActions.propTypes = { children: PropTypes.node }

export function Modal({
  open,
  onClose,
  title,
  description,
  showCloseButton = true,
  className = '',
  style,
  children,
}) {
  const [isMounted, setIsMounted] = useState(open)
  const [isClosing, setIsClosing] = useState(false)
  const titleId = useId()
  const overlayRef = useRef(null)

  useEffect(() => {
    if (open) {
      setIsMounted(true)
      setIsClosing(false)
      return
    }
    if (!isMounted) return
    setIsClosing(true)
    const timeout = setTimeout(() => {
      setIsMounted(false)
      setIsClosing(false)
    }, CLOSE_ANIMATION_MS)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!isMounted || !showCloseButton) return
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isMounted, showCloseButton, onClose])

  if (!isMounted) return null

  const handleOverlayClick = (e) => {
    if (showCloseButton && e.target === overlayRef.current) onClose?.()
  }

  const childArray = Children.toArray(children)
  const footerChild = childArray.find((child) => isValidElement(child) && child.type === ModalFooter)
  const bodyChildren = childArray.filter(
    (child) => !(isValidElement(child) && child.type === ModalFooter)
  )

  return createPortal(
    <div
      ref={overlayRef}
      className={[styles.overlay, isClosing ? styles['overlay--out'] : styles['overlay--in']].join(' ')}
      onClick={handleOverlayClick}
    >
      <div
        className={[styles.modal, isClosing ? styles['modal--out'] : styles['modal--in'], className]
          .filter(Boolean)
          .join(' ')}
        style={style}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={styles.header}>
          <div className={styles.titles}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {description && <p className={styles.desc}>{description}</p>}
          </div>
          {showCloseButton && (
            <button type="button" className={styles.close} aria-label="Cerrar" onClick={() => onClose?.()}>
              <span className="material-icons-outlined">close</span>
            </button>
          )}
        </div>

        <div className={styles.body}>{bodyChildren}</div>

        {footerChild}
      </div>
    </div>,
    document.body
  )
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
}

Modal.Footer = ModalFooter
Modal.Actions = ModalActions
